import {getStorageRef, uploadFile, getDownloadURL, deleteObject} from '@/utils/firebaseUtils';
import {ChecklistItemFormData, ChecklistSubItemFormData, ProcedureChecklist, ProcedureChecklistFormData} from '@/types/ProcedureChecklist';
import {AuditLogService} from './auditLogService';
import {UserService} from './userService';
import {ImageService} from './imageService';
import {UserRole} from "@/types/UserRole";
import {ImageProcessingService} from '@/utils/imageProcessing';
import {ProcedureChecklistRepository} from '@/repositories/ProcedureChecklistRepository';
import { DEFAULT_CATEGORY_ID } from '@/types/Category';
import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import { AppSettingsService } from './appSettingsService';

export class ProcedureChecklistService {
  // Get all procedures/checklists based on user role
  static async getProcedureChecklists(userRole: UserRole): Promise<ProcedureChecklist[]> {
    return ProcedureChecklistRepository.getProcedureChecklists(userRole);
  }

  // Get procedures/checklists by category ID
  static async getProcedureChecklistsByCategory(categoryId: string, userRole: UserRole): Promise<ProcedureChecklist[]> {
    return ProcedureChecklistRepository.getProcedureChecklistsByCategory(categoryId, userRole);
  }

  // Get a single procedure/checklist by ID
  static async getProcedureChecklist(id: string, userRole: UserRole): Promise<ProcedureChecklist | null> {
    const checklist = await ProcedureChecklistRepository.getProcedureChecklist(id);
    
    if (!checklist) {
      return null;
    }

    // Check if non-admin users can access deleted items
    if (userRole !== 'admin' && checklist.isDeleted) {
      return null;
    }

    return checklist;
  }

  // Create a new procedure/checklist (manager and admin only)
  static async createProcedureChecklist(
    formData: ProcedureChecklistFormData, 
    userRole: UserRole, 
    userId: string
  ): Promise<string> {
    if (!this.canModifyProcedures(userRole)) {
      throw new Error('Insufficient permissions to create procedure/checklist');
    }

    try {
      // Process items and upload images
      const processedItems = await this.processChecklistItems(formData.items);

      const checklistData = {
        title: formData.title,
        description: formData.description,
        items: processedItems,
        categories: formData.categories && formData.categories.length > 0 ? formData.categories : [DEFAULT_CATEGORY_ID],
        createdBy: userId,
        updatedBy: userId,
      };

      // Create checklist in repository
      const docId = await ProcedureChecklistRepository.createProcedureChecklist(checklistData, userId);

      // Update procedures timestamp in AppSettings
      await AppSettingsService.updateProceduresLastUpdate();

      // Create audit log entry
      const userEmail = await UserService.getUserEmail(userId);
      await AuditLogService.createAuditLog({
        entityType: 'procedureChecklist',
        entityId: docId,
        action: 'create',
        userId,
        userEmail,
        details: AuditLogService.createChangeDetails('create', 'procedure/checklist'),
        newValues: { ...checklistData, isDeleted: false }
      });

      return docId;
    } catch (error) {
      console.error('Error creating procedure/checklist:', error);
      throw new Error('Failed to create procedure/checklist');
    }
  }

  // Update an existing procedure/checklist (manager and admin only)
  static async updateProcedureChecklist(
    id: string, 
    formData: ProcedureChecklistFormData, 
    userRole: UserRole,
    userId: string
  ): Promise<void> {
    if (!this.canModifyProcedures(userRole)) {
      throw new Error('Insufficient permissions to update procedure/checklist');
    }

    try {
      // Get current checklist data for audit logging
      const currentChecklist = await ProcedureChecklistRepository.getProcedureChecklist(id);
      if (!currentChecklist) {
        throw new Error('Procedure/checklist not found');
      }

      // Process items and upload images
      const processedItems = await this.processChecklistItems(formData.items);

      const updateData = {
        title: formData.title,
        description: formData.description,
        items: processedItems,
        categories: formData.categories && formData.categories.length > 0 ? formData.categories : [DEFAULT_CATEGORY_ID],
        updatedBy: userId,
      };

      // Store previous values for audit log
      const previousValues = { ...currentChecklist };
      const newValues = { ...currentChecklist, ...updateData };

      // Update checklist in repository
      await ProcedureChecklistRepository.updateProcedureChecklist(id, updateData, userId);

      // Update procedures timestamp in AppSettings
      await AppSettingsService.updateProceduresLastUpdate();

      // Create audit log entry
      const userEmail = await UserService.getUserEmail(userId);
      await AuditLogService.createAuditLog({
        entityType: 'procedureChecklist',
        entityId: id,
        action: 'edit',
        userId,
        userEmail,
        details: AuditLogService.createChangeDetails('edit', 'procedure/checklist', { previous: previousValues, new: newValues }),
        previousValues,
        newValues
      });
    } catch (error) {
      console.error('Error updating procedure/checklist:', error);
      throw new Error('Failed to update procedure/checklist');
    }
  }

  // Soft delete a procedure/checklist (manager and admin only)
  static async softDeleteProcedureChecklist(id: string, userRole: UserRole, userId: string): Promise<void> {
    if (!this.canModifyProcedures(userRole)) {
      throw new Error('Insufficient permissions to delete procedure/checklist');
    }

    try {
      // Get current checklist data for audit logging
      const currentChecklist = await ProcedureChecklistRepository.getProcedureChecklist(id);
      if (!currentChecklist) {
        throw new Error('Procedure/checklist not found');
      }

      // Soft delete checklist in repository
      await ProcedureChecklistRepository.softDeleteProcedureChecklist(id, userId);

      // Update procedures timestamp in AppSettings
      await AppSettingsService.updateProceduresLastUpdate();

      // Create audit log entry
      const userEmail = await UserService.getUserEmail(userId);
      await AuditLogService.createAuditLog({
        entityType: 'procedureChecklist',
        entityId: id,
        action: 'delete',
        userId,
        userEmail,
        details: AuditLogService.createChangeDetails('delete', 'procedure/checklist'),
        previousValues: currentChecklist
      });
    } catch (error) {
      console.error('Error deleting procedure/checklist:', error);
      throw new Error('Failed to delete procedure/checklist');
    }
  }

  // Restore a soft-deleted procedure/checklist (admin only)
  static async restoreProcedureChecklist(id: string, userRole: UserRole, userId: string): Promise<void> {
    if (userRole !== 'admin') {
      throw new Error('Insufficient permissions to restore procedure/checklist');
    }

    try {
      // Get current checklist data for audit logging
      const currentChecklist = await ProcedureChecklistRepository.getProcedureChecklist(id);
      if (!currentChecklist) {
        throw new Error('Procedure/checklist not found');
      }

      // Restore checklist in repository
      await ProcedureChecklistRepository.restoreProcedureChecklist(id, userId);

      // Update procedures timestamp in AppSettings
      await AppSettingsService.updateProceduresLastUpdate();

      // Create audit log entry
      const userEmail = await UserService.getUserEmail(userId);
      await AuditLogService.createAuditLog({
        entityType: 'procedureChecklist',
        entityId: id,
        action: 'restore',
        userId,
        userEmail,
        details: AuditLogService.createChangeDetails('restore', 'procedure/checklist'),
        previousValues: currentChecklist
      });
    } catch (error) {
      console.error('Error restoring procedure/checklist:', error);
      throw new Error('Failed to restore procedure/checklist');
    }
  }

  // Upload image to Firebase Storage - delegated to ImageService
  static async uploadImage(imageUri: string, fileName: string): Promise<string> {
    return ImageService.uploadImage(imageUri, fileName, 'procedures_checklists/images');
  }

  // Delete image from Firebase Storage - delegated to ImageService
  static async deleteImage(imageUrl: string): Promise<void> {
    return ImageService.deleteImage(imageUrl);
  }

  // Process checklist items, handling image uploads
  private static async processChecklistItems(items: ChecklistItemFormData[]): Promise<any[]> {
    const processedItems = [];

    for (const item of items) {
      const processedItem: any = {
        id: item.id,
        topic: item.topic,
        content: item.content,
        number: item.number,
      };

      // Only include link if it has a value
      if (item.link && item.link.trim()) {
        processedItem.link = item.link;
      }

      // Only include file if it has a value
      if (item.file && item.file.trim()) {
        processedItem.file = item.file;
      }

      // Handle image upload if it's a new image (base64 or local file)
      if (item.image && (item.image.startsWith('data:') || item.image.startsWith('file:'))) {
        try {
          const fileName = `${Date.now()}_${item.id}.jpg`;
          processedItem.image = await this.uploadImage(item.image, fileName);
        } catch (error) {
          console.error('Error uploading image for item:', item.id, error);
          // Continue without image if upload fails
        }
      } else if (item.image && item.image.trim() && !item.image.startsWith('blob:')) {
        // Existing image URL - only include if it has a value and is not a blob URL
        processedItem.image = item.image;
      } else if (item.image && item.image.startsWith('blob:')) {
        // Log warning if blob URL detected (this indicates a bug in the UI layer)
        console.warn('Blob URL detected in procedure form data, skipping:', item.image);
        // Don't include blob URLs in the saved data
      }

      // Recursively process sub-items if present
      if (item.subItems && item.subItems.length > 0) {
        processedItem.subItems = await this.processSubItems(item.subItems);
      }

      processedItems.push(processedItem);
    }

    return processedItems;
  }

  // Recursively process sub-items, handling image uploads
  private static async processSubItems(subItems: ChecklistSubItemFormData[]): Promise<any[]> {
    const processedSubItems = [];

    for (const subItem of subItems) {
      const processedSubItem: any = {
        id: subItem.id,
        topic: subItem.topic,
      };

      if (subItem.type) {
        processedSubItem.type = subItem.type;
      }

      if (subItem.content && subItem.content.trim()) {
        processedSubItem.content = subItem.content;
      }

      if (subItem.control && subItem.control.trim()) {
        processedSubItem.control = subItem.control;
      }

      if (subItem.requiredState && subItem.requiredState.trim()) {
        processedSubItem.requiredState = subItem.requiredState;
      }

      if (subItem.link && subItem.link.trim()) {
        processedSubItem.link = subItem.link;
      }

      // Handle image upload for sub-items
      if (subItem.image && (subItem.image.startsWith('data:') || subItem.image.startsWith('file:'))) {
        try {
          const fileName = `${Date.now()}_${subItem.id}.jpg`;
          processedSubItem.image = await this.uploadImage(subItem.image, fileName);
        } catch (error) {
          console.error('Error uploading image for sub-item:', subItem.id, error);
        }
      } else if (subItem.image && subItem.image.trim() && !subItem.image.startsWith('blob:')) {
        processedSubItem.image = subItem.image;
      }

      // Recursively process nested sub-items
      if (subItem.subItems && subItem.subItems.length > 0) {
        processedSubItem.subItems = await this.processSubItems(subItem.subItems);
      }

      processedSubItems.push(processedSubItem);
    }

    return processedSubItems;
  }

  // Check if user can modify procedures/checklists
  private static canModifyProcedures(userRole: UserRole): boolean {
    return userRole === 'manager' || userRole === 'admin';
  }
}
